const pad2 = (n: number) => String(n).padStart(2, '0');

/** Clock face: `25:00`, or `1:04:32` once past an hour. Always whole seconds. */
export function formatClock(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(sec)}` : `${pad2(m)}:${pad2(sec)}`;
}

/** Human duration for stats and rows: `1h 20m`, `48m`, `2h`, or `—` for zero/null. */
export function formatDuration(totalSec: number | null | undefined, { zero = '—' } = {}): string {
  if (totalSec == null || totalSec < 30) return zero;
  const m = Math.round(totalSec / 60);
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

/** Compact form for narrow rows: `1h20`, `48m`. */
export function formatDurationCompact(totalSec: number | null | undefined): string {
  if (totalSec == null || totalSec < 30) return '—';
  const m = Math.round(totalSec / 60);
  const h = Math.floor(m / 60);
  if (h === 0) return `${m}m`;
  return m % 60 === 0 ? `${h}h` : `${h}h${pad2(m % 60)}`;
}

/**
 * Parse a typed estimate or length into seconds. Accepts `90` (minutes), `1h30`, `1h 30m`, `1.5h`, `2h`, `45m`, `1:30`.
 * Returns null for anything it cannot read or for non-positive values.
 */
export function parseDuration(input: string): number | null {
  const s = input.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(\d+):([0-5]?\d)$/))) return toSec(+m[1]! * 60 + +m[2]!);
  if ((m = s.match(/^(\d+(?:\.\d+)?)h(?:(\d+)m?)?$/))) return toSec(+m[1]! * 60 + (m[2] ? +m[2] : 0));
  if ((m = s.match(/^(\d+(?:\.\d+)?)m?$/))) return toSec(+m[1]!);
  return null;
}
const toSec = (minutes: number) => (minutes > 0 && Number.isFinite(minutes) ? Math.round(minutes * 60) : null);

export const MINUTE = 60;
export const HOUR = 3600;

/** "1h 20m / 2h" with an estimate, "48m" without, plus the overage when past the estimate (design 8b). */
export function formatLogged(trackedSec: number, estimateSec: number | null): { text: string; over: string | null; ratio: number | null } {
  const logged = formatDuration(trackedSec, { zero: '—' });
  if (!estimateSec) return { text: logged, over: null, ratio: null };
  const over = trackedSec > estimateSec ? `+${formatDuration(trackedSec - estimateSec, { zero: '' })} over` : null;
  return { text: `${logged} / ${formatDuration(estimateSec)}`, over, ratio: Math.min(1, trackedSec / estimateSec) };
}
